import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { toast } from "@volca/ui/lib/toast";
import { ActionReviewCard } from "@/accounts-payable/invoice-review-page/action-review-card";
import {
  useBulkApproveAgentActions,
  useGetDocumentAgentRuns,
  useUpdateAgentAction,
} from "@/accounts-payable/query";
import type { ApDocumentType } from "@/accounts-payable/types";
import {
  AUTOMATED_RUN_BADGE,
  getActiveRun,
  getBulkApproveIds,
  getLatestCompletedRun,
  isAutomatedRun,
  isBulkApprovable,
  isPendingReviewAction,
  runsInFlightRefetchInterval,
} from "@/accounts-payable/utils";
import { getErrorMessage } from "@/core/api/errors";
import { ActionListSkeleton } from "./action-list-skeleton";
import { CollapsibleGroup } from "./collapsible-group";
import { AgentRunningNotice, StatusBadge } from "./shared";
import { StaleDataNotice } from "./stale-data-notice";
import { useResolveWithBasisCheck } from "./use-resolve-with-basis-check";
/** The agent's proposals for one purchase document, reviewable in place. */
export const DocumentReviewActions = ({
  documentId,
  documentType,
  documentHasJob,
  businessId,
}: {
  documentId: string;
  documentType: ApDocumentType;
  /** Whether the document has a job, whose POs a PO pick opens on. */
  documentHasJob: boolean;
  businessId: string | null;
}) => {
  // Poll every 3s while the runs are in flight, so cards reach their terminal
  // state without a manual refresh.
  const {
    data: runs = [],
    isLoading: runsLoading,
    isError: runsError,
  } = useGetDocumentAgentRuns(
    businessId,
    documentType,
    documentId,
    runsInFlightRefetchInterval,
  );
  const resolve = useResolveWithBasisCheck(businessId);
  const update = useUpdateAgentAction(businessId);
  const bulkApprove = useBulkApproveAgentActions(businessId);

  const run = getLatestCompletedRun(runs);
  const activeRun = getActiveRun(runs);
  const actions = run?.actions ?? [];
  const pendingActions = actions.filter(isPendingReviewAction);
  // "Approve all" is meaningless when the rest are acknowledgements (the
  // Review tab's rule); it settles every pending row, flags included, except
  // a blocked PO, which stays pending with its reason.
  const approvableCount = pendingActions.filter(isBulkApprovable).length;
  const resolvedActions = actions.filter((a) => !isPendingReviewAction(a));

  return (
    <Stack className="w-full" align="start" gap={4}>
      {resolve.dialog}
      <Stack
        direction="row"
        align="center"
        justify="between"
        gap={4}
        className="w-full flex-wrap"
      >
        <Stack direction="row" gap={2} align="center" className="flex-wrap">
          <Text variant="title">
            Agent actions{actions.length ? ` (${actions.length})` : ""}
          </Text>
          {run && isAutomatedRun(run) && (
            <StatusBadge meta={AUTOMATED_RUN_BADGE} />
          )}
        </Stack>
        {approvableCount > 1 && (
          <Button
            variant="outline"
            onClick={() =>
              // The mutation says what went through if it fails.
              bulkApprove.mutate(getBulkApproveIds(actions))
            }
            disabled={bulkApprove.isPending}
            aria-busy={bulkApprove.isPending || undefined}
          >
            {bulkApprove.isPending && (
              <Spinner aria-hidden data-icon="inline-start" />
            )}
            Approve all
          </Button>
        )}
      </Stack>
      {/*
            Above the list, not instead of it: a re-match leaves the previous
            run's proposals on screen while the new run works, and those are
            about to be superseded. Without a standing notice the page looks
            settled and the stale proposals look current.
          */}
      {activeRun ? (
        <AgentRunningNotice />
      ) : (
        <StaleDataNotice run={run} actions />
      )}
      {actions.length > 0 ? (
        // Open work leads; resolved collapses while anything is pending.
        <Stack className="w-full" align="start" gap={6}>
          {[
            {
              label: "Needs review",
              items: pendingActions,
              defaultOpen: true,
            },
            {
              label: "Resolved",
              items: resolvedActions,
              defaultOpen: pendingActions.length === 0,
            },
          ].map((group) => (
            <CollapsibleGroup
              // Keyed by document so stepping through the queue resets a
              // manual open/close choice.
              key={`${documentId}-${group.label}`}
              label={group.label}
              count={group.items.length}
              defaultOpen={group.defaultOpen}
            >
              <Card className="flex w-full flex-col gap-0 divide-y overflow-hidden py-0">
                {group.items.map((a) => (
                  <ActionReviewCard
                    key={a.id}
                    action={a}
                    runActions={actions}
                    businessId={businessId}
                    document={{
                      id: documentId,
                      type: documentType,
                      agentRunId: (activeRun ?? run)?.id ?? null,
                      hasJob: documentHasJob,
                    }}
                    onResolve={(decision) => resolve.resolve(a.id, decision)}
                    onSave={(payload, onSaved) =>
                      update.mutate(
                        { actionId: a.id, payload },
                        {
                          onSuccess: onSaved,
                          // e.g. a bill's vendor or account QuickBooks no
                          // longer has (422).
                          onError: (error) =>
                            toast.error("Couldn’t save your changes", {
                              description: getErrorMessage(error),
                            }),
                        },
                      )
                    }
                    resolving={resolve.resolvingIds.has(a.id)}
                    saving={update.isPending}
                  />
                ))}
              </Card>
            </CollapsibleGroup>
          ))}
        </Stack>
      ) : runsError ? (
        // Say the load failed rather than claim there are none (the
        // sidebar's rule); a still-loading list says nothing yet.
        <Text tone="muted">Couldn’t load agent actions. Please try again.</Text>
      ) : runsLoading ? (
        <ActionListSkeleton />
      ) : (
        !activeRun && (
          <Empty className={emptyStateVariants({ layout: "inline" })}>
            <EmptyHeader>
              <EmptyDescription>
                No agent actions for this {documentType} yet.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )
      )}
    </Stack>
  );
};
